// SignUp.jsx
import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Button, Card, Form, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import { Navigate, Link } from "react-router-dom";
import "./SignUp.css";

const schema = Yup.object({
  email: Yup.string().email("Email no válido").required("Requerido"),
  password1: Yup.string().min(8, "Mínimo 8 caracteres").required("Requerido"),
  password2: Yup.string().oneOf([Yup.ref("password1")], "No coincide").required("Requerido"),
  first_name: Yup.string().max(40).optional(),
  terms: Yup.boolean().oneOf([true], "Acepta los términos"),
});

function normalizeServerErrors(err) {
  // Network / timeout
  if (err?.code === "ECONNABORTED") {
    return { formError: "El servidor tardó demasiado en responder. Intenta de nuevo." };
  }
  if (!err?.response) {
    return { formError: "Sin conexión con el servidor. Revisa tu Internet e inténtalo otra vez." };
  }

  const { status, data } = err.response;
  // Tu vista envía { message: serializer.errors } en 400
  const payload = data?.message ?? data ?? {};

  // DRF puede enviar {detail: "..."} o {non_field_errors: ["..."]}
  let formError =
    (Array.isArray(payload?.non_field_errors) && payload.non_field_errors.join(" ")) ||
    (typeof payload?.detail === "string" && payload.detail) ||
    (typeof payload === "string" ? payload : "");

  // Campos comunes del serializer
  const fieldErrors = {};
  const candidateFields = ["email", "password1", "password2", "username", "first_name", "last_name", "telephone"];
  candidateFields.forEach((f) => {
    const v = payload?.[f];
    if (Array.isArray(v) && v.length) fieldErrors[f] = v.join(" ");
    if (typeof v === "string" && v) fieldErrors[f] = v;
  });

  // Stripe o mensajes generales llegaron como array en 'message' directamente
  if (!formError && status >= 500) formError = "Ocurrió un error en el servidor. Intenta más tarde.";
  if (!formError && status === 400 && Object.keys(fieldErrors).length === 0) {
    formError = "Revisa los datos del formulario.";
  }
  return { fieldErrors, formError };
}

export default function SignUp({ isLoggedIn }) {
  const [ok, setOk] = useState(false);
  const [serverMsg, setServerMsg] = useState(""); // banner superior
  const [loading, setLoading] = useState(false);

  if (isLoggedIn || ok) return <Navigate to="/log-in" replace />;

  return (
    <div className="auth-center">
      <Card className="auth-card">
        <Card.Body>
          <h1 className="auth-title">Crear cuenta</h1>

          {serverMsg && (
            <Alert variant="warning" onClose={() => setServerMsg("")} dismissible aria-live="polite">
              {serverMsg}
            </Alert>
          )}

          <Formik
            initialValues={{ email: "", password1: "", password2: "", first_name: "", terms: false }}
            validationSchema={schema}
            onSubmit={async (v, { setErrors, setSubmitting }) => {
              setLoading(true);
              setServerMsg("");
              try {
                const fd = new FormData();
                fd.append("email", v.email);
                fd.append("password1", v.password1);
                fd.append("password2", v.password2);
                if (v.first_name) fd.append("first_name", v.first_name);

                await axios.post(`${process.env.REACT_APP_BASE_URL}/api/sign_up/`, fd, { timeout: 8000 });
                setOk(true);
              } catch (e) {
                const { fieldErrors, formError } = normalizeServerErrors(e);
                if (fieldErrors && Object.keys(fieldErrors).length) setErrors(fieldErrors);
                if (formError) setServerMsg(formError);
              } finally {
                setSubmitting(false);
                setLoading(false);
              }
            }}
          >
            {({ handleSubmit, handleChange, values, errors, touched, isSubmitting }) => (
              <Form noValidate onSubmit={handleSubmit} aria-live="polite">
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    name="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={values.email}
                    onChange={handleChange}
                    isInvalid={touched.email && !!errors.email}
                    placeholder="tu@email.com"
                  />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Contraseña</Form.Label>
                  <Form.Control
                    name="password1"
                    type="password"
                    autoComplete="new-password"
                    value={values.password1}
                    onChange={handleChange}
                    isInvalid={touched.password1 && !!errors.password1}
                    placeholder="Mínimo 8 caracteres"
                  />
                  <Form.Control.Feedback type="invalid">{errors.password1}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Repite contraseña</Form.Label>
                  <Form.Control
                    name="password2"
                    type="password"
                    autoComplete="new-password"
                    value={values.password2}
                    onChange={handleChange}
                    isInvalid={touched.password2 && !!errors.password2}
                  />
                  <Form.Control.Feedback type="invalid">{errors.password2}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    name="terms"
                    checked={values.terms}
                    onChange={handleChange}
                    label={
                      <span>
                        Acepto <Link to="/terms-sections">Términos</Link> y <Link to="/privacy-policy">Privacidad</Link>
                      </span>
                    }
                    isInvalid={touched.terms && !!errors.terms}
                  />
                  {touched.terms && errors.terms && (
                    <div className="invalid-feedback d-block">{errors.terms}</div>
                  )}
                </Form.Group>

                <Button className="auth-btn" type="submit" disabled={loading || isSubmitting}>
                  {loading || isSubmitting ? (
                    <>
                      <Spinner size="sm" animation="border" /> Creando…
                    </>
                  ) : (
                    "Crear cuenta"
                  )}
                </Button>

                <div className="auth-small">
                  ¿Ya tienes cuenta? <Link to="/log-in">Inicia sesión</Link>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </div>
  );
}
